import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { IdentityBannerComponent } from '../../../ui/views/identity-banner/identity-banner.component';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import { AuthService } from '../../../../core/services/utils/auth.service';
import {
  ReportCardService,
  StudentDocument,
} from '../../../../core/services/student-files/report-card.service';
import { Student } from '../../../../core/interfaces/student';

/** Taille maximale acceptée par le back pour un document de dossier élève. */
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

@Component({
  selector: 'app-student-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    IdentityBannerComponent,
    ErrorStateComponent,
  ],
  templateUrl: './student-profile.component.html',
  styleUrl: './student-profile.component.css',
})
export class StudentProfileComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly reportCards = inject(ReportCardService);

  readonly student = signal<Student | null>(null);
  readonly displayName = signal('');
  readonly avatar = signal('assets/images/default-user.png');

  readonly documents = signal<StudentDocument[]>([]);
  readonly loading = signal(true);
  readonly errored = signal(false);

  readonly uploading = signal(false);
  readonly uploadError = signal<string | null>(null);

  selectedFile: File | null = null;
  title = '';
  documentDate = '';

  async ngOnInit(): Promise<void> {
    await this.loadIdentity();
    this.loadDocuments();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
    this.uploadError.set(null);

    // Contrôle local : inutile de faire monter 40 Mo pour se voir refusé.
    if (this.selectedFile && this.selectedFile.size > MAX_SIZE_BYTES) {
      this.uploadError.set('PROFILE.REPORT_CARDS.TOO_LARGE');
      this.selectedFile = null;
      input.value = '';
      return;
    }

    // Le titre reprend le nom du fichier, que l'élève peut corriger : lui
    // demander de le ressaisir n'apporterait rien.
    if (this.selectedFile && !this.title.trim()) {
      this.title = this.selectedFile.name.replace(/\.[^.]+$/, '');
    }
  }

  get canSubmit(): boolean {
    return !!this.selectedFile && !!this.title.trim() && !this.uploading();
  }

  submit(): void {
    if (!this.canSubmit) return;

    this.uploading.set(true);
    this.uploadError.set(null);

    this.reportCards
      .upload(this.selectedFile!, this.title.trim(), this.documentDate || null)
      .subscribe({
        next: (res) => {
          if (res?.data) this.documents.update((list) => [res.data!, ...list]);
          this.reset();
          this.uploading.set(false);
        },
        error: (err) => {
          this.uploadError.set(
            (err as { error?: { message?: string } })?.error?.message
              ?? 'PROFILE.REPORT_CARDS.UPLOAD_ERROR',
          );
          this.uploading.set(false);
        },
      });
  }

  reload(): void {
    this.loadDocuments();
  }

  /** Taille lisible ; vide si le serveur ne l'a pas renvoyée. */
  sizeLabel(bytes: number | null | undefined): string {
    if (bytes == null || bytes <= 0) return '';
    const units = ['B', 'KB', 'MB'];
    let i = 0;
    let v = bytes;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i++;
    }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
  }

  private reset(): void {
    this.selectedFile = null;
    this.title = '';
    this.documentDate = '';
  }

  private async loadIdentity(): Promise<void> {
    try {
      this.displayName.set(await this.auth.getUserDisplayName());
      this.avatar.set(await this.auth.getUserAvatar());
      this.student.set(await this.auth.getStudent());
    } catch {
      // L'identité manquante ne bloque pas le dépôt : le serveur reconnaît
      // l'élève par son jeton, pas par ce que l'écran affiche.
      this.student.set(null);
    }
  }

  private loadDocuments(): void {
    this.loading.set(true);
    this.errored.set(false);

    this.reportCards.list().subscribe({
      next: (res) => {
        this.documents.set(res?.data ?? []);
        this.loading.set(false);
      },
      error: () => {
        // Une liste vide reste vide : aucun bulletin de démonstration.
        this.documents.set([]);
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }
}
